import React, { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Printer,
  Mail,
  Download,
  FileText,
  ShoppingCart,
  Receipt,
  Building2,
  Phone,
  Globe,
  MapPin,
  Languages
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useSettings } from '../context/SettingsContext';
import SARSymbol from './SARSymbol';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document type configurations with bilingual labels
const DOC_CONFIG = {
  quotation: {
    title: 'Quotation',
    titleAr: 'عرض سعر',
    icon: FileText,
    color: 'blue',
    numberLabel: 'Quotation No.',
    numberLabelAr: 'رقم العرض'
  },
  sales_order: {
    title: 'Sales Order',
    titleAr: 'أمر بيع',
    icon: ShoppingCart,
    color: 'purple',
    numberLabel: 'Order No.',
    numberLabelAr: 'رقم الطلب'
  },
  invoice: {
    title: 'Invoice',
    titleAr: 'فاتورة',
    icon: Receipt,
    color: 'emerald',
    numberLabel: 'Invoice No.',
    numberLabelAr: 'رقم الفاتورة'
  }
};

// Bilingual labels for document elements
const LABELS = {
  en: {
    billTo: 'Bill To',
    shipTo: 'Ship To',
    date: 'Date',
    dueDate: 'Due Date',
    validUntil: 'Valid Until',
    item: 'Item',
    description: 'Description',
    qty: 'Qty',
    unitPrice: 'Unit Price',
    amount: 'Amount',
    subtotal: 'Subtotal',
    discount: 'Discount',
    tax: 'Tax',
    total: 'Total',
    notes: 'Notes',
    terms: 'Terms & Conditions',
    thankYou: 'Thank you for your business!',
    page: 'Page',
    of: 'of',
    phone: 'Phone',
    email: 'Email',
    website: 'Website',
    taxNo: 'Tax No.',
    crNo: 'CR No.'
  },
  ar: {
    billTo: 'فاتورة إلى',
    shipTo: 'شحن إلى',
    date: 'التاريخ',
    dueDate: 'تاريخ الاستحقاق',
    validUntil: 'صالح حتى',
    item: 'الصنف',
    description: 'الوصف',
    qty: 'الكمية',
    unitPrice: 'سعر الوحدة',
    amount: 'المبلغ',
    subtotal: 'المجموع الفرعي',
    discount: 'الخصم',
    tax: 'الضريبة',
    total: 'الإجمالي',
    notes: 'ملاحظات',
    terms: 'الشروط والأحكام',
    thankYou: 'شكراً لتعاملكم معنا!',
    page: 'صفحة',
    of: 'من',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    website: 'الموقع الإلكتروني',
    taxNo: 'الرقم الضريبي',
    crNo: 'السجل التجاري'
  }
};

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  confirmed: 'bg-blue-100 text-blue-700',
  processing: 'bg-amber-100 text-amber-700',
  shipped: 'bg-purple-100 text-purple-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  invoiced: 'bg-indigo-100 text-indigo-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-700'
};

// Bilingual text component - moved outside to prevent re-creation on each render
const BilingualText = ({ labelKey, className = '', isArabicOnly, isEnglishOnly, isBilingual, arabicPrimary }) => {
  if (isArabicOnly) {
    return <span className={`font-arabic ${className}`}>{LABELS.ar[labelKey]}</span>;
  }
  if (isEnglishOnly) {
    return <span className={className}>{LABELS.en[labelKey]}</span>;
  }
  // Bilingual
  return (
    <span className={className}>
      {arabicPrimary ? (
        <>
          <span className="font-arabic">{LABELS.ar[labelKey]}</span>
          <span className="text-muted-foreground text-xs ml-1">({LABELS.en[labelKey]})</span>
        </>
      ) : (
        <>
          {LABELS.en[labelKey]}
          <span className="font-arabic text-muted-foreground text-xs mr-1"> ({LABELS.ar[labelKey]})</span>
        </>
      )}
    </span>
  );
};

// Bilingual Printable Document Component
const PrintableDocument = React.forwardRef(({ 
  document, 
  type, 
  template, 
  companyInfo,
  currencySymbol,
  currencyCode,
  documentLanguage = 'bilingual_en',
  settings = {}
}, ref) => {
  const config = DOC_CONFIG[type];
  const primaryColor = template?.branding?.primary_color || '#3b82f6';
  const displaySymbol = currencySymbol || 'SAR';
  
  // Determine language mode
  const isArabicOnly = documentLanguage === 'ar';
  const isEnglishOnly = documentLanguage === 'en';
  const isBilingual = documentLanguage.startsWith('bilingual');
  const arabicPrimary = documentLanguage === 'bilingual_ar';
  
  // Helper to render bilingual text with current language settings
  const renderBilingualText = (labelKey, className = '') => (
    <BilingualText 
      labelKey={labelKey} 
      className={className}
      isArabicOnly={isArabicOnly}
      isEnglishOnly={isEnglishOnly}
      isBilingual={isBilingual}
      arabicPrimary={arabicPrimary}
    />
  );

  // Get document title
  const getDocTitle = () => {
    if (isArabicOnly) return config.titleAr;
    if (isEnglishOnly) return config.title;
    return arabicPrimary 
      ? `${config.titleAr} / ${config.title}`
      : `${config.title} / ${config.titleAr}`;
  };

  // Check if current currency is SAR for using official symbol
  const currencyIsSAR = settings?.currency === 'SAR' || displaySymbol === 'SAR';

  // Format currency with symbol - returns JSX for SAR to use official symbol
  const formatCurrency = (amount) => {
    const value = Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    if (currencyIsSAR) {
      return (
        <span className="inline-flex items-center gap-1">
          <SARSymbol size={14} color="currentColor" />
          <span>{value}</span>
        </span>
      );
    }
    return `${displaySymbol} ${value}`;
  };

  const docNumber = document?.quote_number || document?.order_number || document?.invoice_number;
  const isRTL = isArabicOnly || arabicPrimary;

  return (
    <div 
      ref={ref} 
      className={`bg-white p-8 min-h-[297mm] w-[210mm] mx-auto text-black print:p-6 ${isRTL ? 'font-arabic' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ fontFamily: isRTL ? "'Almarai', sans-serif" : "'Inter', sans-serif" }}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-8 pb-4 border-b-2" style={{ borderColor: primaryColor }}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          {template?.branding?.logo_url ? (
            <img src={template.branding.logo_url} alt="Company Logo" className="h-16 mb-2" />
          ) : settings?.logo_url ? (
            <img src={settings.logo_url} alt="Company Logo" className="h-16 mb-2" />
          ) : (
            <div className="text-2xl font-bold mb-2" style={{ color: primaryColor }}>
              {isRTL && settings?.company_name_ar ? settings.company_name_ar : (settings?.company_name || companyInfo?.name || 'Company Name')}
              {isBilingual && (
                <div className="text-sm font-normal text-gray-500">
                  {arabicPrimary ? (settings?.company_name || companyInfo?.name) : settings?.company_name_ar}
                </div>
              )}
            </div>
          )}
          <div className="text-sm text-gray-600 space-y-1">
            <p className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {isRTL && settings?.address_ar ? settings.address_ar : (settings?.address || companyInfo?.address || 'Address')}
              {settings?.city && `, ${isRTL && settings?.city_ar ? settings.city_ar : settings.city}`}
            </p>
            {isBilingual && (settings?.address_ar || settings?.address) && (
              <p className="text-xs text-gray-400">
                {arabicPrimary ? settings?.address : settings?.address_ar}
              </p>
            )}
            {(settings?.phone || companyInfo?.phone) && (
              <p className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {settings?.phone || companyInfo?.phone}
              </p>
            )}
            {(settings?.website || companyInfo?.website) && (
              <p className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                {settings?.website || companyInfo?.website}
              </p>
            )}
          </div>
        </div>
        
        <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
          <h1 className="text-3xl font-bold mb-2" style={{ color: primaryColor }}>
            {getDocTitle()}
          </h1>
          <div className="text-sm space-y-1">
            <p className="font-semibold">
              {renderBilingualText(type === 'invoice' ? 'taxNo' : 'date')} #{docNumber}
            </p>
            <p>
              {renderBilingualText('date')}: {new Date(document?.issue_date || document?.created_at).toLocaleDateString()}
            </p>
            {document?.due_date && (
              <p>
                {renderBilingualText('dueDate')}: {new Date(document.due_date).toLocaleDateString()}
              </p>
            )}
            {document?.valid_until && (
              <p>
                {renderBilingualText('validUntil')}: {new Date(document.valid_until).toLocaleDateString()}
              </p>
            )}
          </div>
          {/* Tax Registration Numbers */}
          {(settings?.tax_number || settings?.cr_number) && (
            <div className="mt-2 text-xs text-gray-500 border-t pt-2">
              {settings?.tax_number && (
                <p>{renderBilingualText('taxNo')}: {settings.tax_number}</p>
              )}
              {settings?.cr_number && (
                <p>{renderBilingualText('crNo')}: {settings.cr_number}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bill To Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-700 mb-2 pb-1 border-b">
          {renderBilingualText('billTo')}
        </h3>
        <div className="text-sm">
          <p className="font-semibold">{document?.client_name}</p>
          {document?.client_name_ar && isBilingual && (
            <p className="font-arabic text-gray-500">{document.client_name_ar}</p>
          )}
          {document?.client_address && <p>{document.client_address}</p>}
          {document?.client_email && <p>{document.client_email}</p>}
          {document?.client_phone && <p>{document.client_phone}</p>}
        </div>
      </div>

      {/* Items Table */}
      <table className="w-full mb-6 text-sm">
        <thead>
          <tr className="text-white" style={{ backgroundColor: primaryColor }}>
            <th className={`p-2 ${isRTL ? 'text-right' : 'text-left'}`}>
              {renderBilingualText('item')}
            </th>
            <th className={`p-2 ${isRTL ? 'text-right' : 'text-left'} w-1/3`}>
              {renderBilingualText('description')}
            </th>
            <th className="p-2 text-center w-16">
              {renderBilingualText('qty')}
            </th>
            <th className={`p-2 ${isRTL ? 'text-left' : 'text-right'} w-24`}>
              {renderBilingualText('unitPrice')}
            </th>
            <th className={`p-2 ${isRTL ? 'text-left' : 'text-right'} w-28`}>
              {renderBilingualText('amount')}
            </th>
          </tr>
        </thead>
        <tbody>
          {(document?.items || document?.line_items || []).map((item, index) => (
            <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              <td className={`p-2 border-b ${isRTL ? 'text-right' : 'text-left'}`}>
                {item.name || item.product_name || item.item_name || `Item ${index + 1}`}
                {item.name_ar && isBilingual && (
                  <div className="font-arabic text-xs text-gray-500">{item.name_ar}</div>
                )}
              </td>
              <td className={`p-2 border-b ${isRTL ? 'text-right' : 'text-left'}`}>
                <div>{item.description || '-'}</div>
                {item.description_ar && isBilingual && (
                  <div className="font-arabic text-xs text-gray-500">{item.description_ar}</div>
                )}
              </td>
              <td className="p-2 border-b text-center">{item.quantity}</td>
              <td className={`p-2 border-b ${isRTL ? 'text-left' : 'text-right'}`}>
                {formatCurrency(item.unit_price || item.price)}
              </td>
              <td className={`p-2 border-b ${isRTL ? 'text-left' : 'text-right'} font-semibold`}>
                {formatCurrency((item.quantity || 1) * (item.unit_price || item.price || 0))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} mb-8`}>
        <div className="w-72">
          <div className="flex justify-between py-1 text-sm">
            {renderBilingualText('subtotal')}
            <span className="font-mono">{formatCurrency(document?.subtotal)}</span>
          </div>
          {document?.discount_amount > 0 && (
            <div className="flex justify-between py-1 text-sm text-red-600">
              {renderBilingualText('discount')}
              <span className="font-mono">-{formatCurrency(document.discount_amount)}</span>
            </div>
          )}
          {(document?.tax_amount > 0 || document?.tax > 0) && (
            <div className="flex justify-between py-1 text-sm">
              <span>
                {renderBilingualText('tax')} ({document?.tax_rate || settings?.tax_rate || 15}%)
                {isBilingual && settings?.tax_name_ar && (
                  <span className="font-arabic text-xs text-gray-500 mx-1">
                    ({settings.tax_name_ar})
                  </span>
                )}
              </span>
              <span className="font-mono">{formatCurrency(document.tax_amount || document.tax)}</span>
            </div>
          )}
          {document?.shipping_cost > 0 && (
            <div className="flex justify-between py-1 text-sm">
              <span>Shipping / الشحن</span>
              <span className="font-mono">{formatCurrency(document.shipping_cost)}</span>
            </div>
          )}
          <div 
            className="flex justify-between py-2 border-t-2 font-bold text-lg mt-2" 
            style={{ borderColor: primaryColor }}
          >
            {renderBilingualText('total')}
            <span className="font-mono" style={{ color: primaryColor }}>
              {formatCurrency(document?.total)}
            </span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {document?.notes && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-sm mb-2">
            {renderBilingualText('notes')}
          </h4>
          <p className="text-sm text-gray-600">{document.notes}</p>
          {document.notes_ar && isBilingual && (
            <p className="font-arabic text-sm text-gray-500 mt-1">{document.notes_ar}</p>
          )}
        </div>
      )}

      {/* Terms & Conditions */}
      {(settings?.terms_conditions || settings?.terms_conditions_ar) && (
        <div className="mb-6 p-4 border rounded-lg text-xs">
          <h4 className="font-semibold mb-2">
            {renderBilingualText('terms')}
          </h4>
          <p className="text-gray-600">
            {isRTL ? settings.terms_conditions_ar : settings.terms_conditions}
          </p>
          {isBilingual && (
            <p className={`${arabicPrimary ? '' : 'font-arabic'} text-gray-500 mt-2`}>
              {arabicPrimary ? settings.terms_conditions : settings.terms_conditions_ar}
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="border-t pt-4 mt-8 text-center text-sm text-gray-500">
        <p className="font-semibold" style={{ color: primaryColor }}>
          {isArabicOnly ? 'شكراً لتعاملكم معنا!' : 
           isEnglishOnly ? 'Thank you for your business!' :
           'Thank you for your business! / شكراً لتعاملكم معنا!'}
        </p>
        <p className="mt-1">
          {settings?.company_name || companyInfo?.name}
          {isBilingual && settings?.company_name_ar && ` / ${settings.company_name_ar}`}
        </p>
      </div>
    </div>
  );
});

PrintableDocument.displayName = 'PrintableDocument';

// Email Dialog Component
const EmailDialog = ({ open, onClose, document, type, onSend, currencySymbol, companyName }) => {
  const [emailData, setEmailData] = useState({
    to: document?.client_email || '',
    cc: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  
  const config = DOC_CONFIG[type];
  const docNumber = document?.quote_number || document?.order_number || document?.invoice_number;
  const displaySymbol = currencySymbol || 'SAR';
  const displayCompany = companyName || 'Your Company';
  
  React.useEffect(() => {
    if (document) {
      setEmailData(prev => ({
        ...prev,
        to: document.client_email || '',
        subject: `${config.title} ${docNumber} from ${displayCompany}`,
        message: `Dear ${document.client_name || 'Customer'},\n\nPlease find attached ${config.title.toLowerCase()} ${docNumber}.\n\nTotal Amount: ${displaySymbol} ${document.total?.toFixed(2)}\n\nIf you have any questions, please don't hesitate to contact us.\n\nBest regards,\n${displayCompany}`
      }));
    }
  }, [document, type, displaySymbol, displayCompany, config.title, docNumber]);

  const handleSend = async () => {
    if (!emailData.to) {
      toast({ title: 'Error', description: 'Please enter recipient email', variant: 'destructive' });
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/documents/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_type: type,
          document_id: document.id,
          ...emailData
        })
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Email sent successfully' });
        onClose();
        if (onSend) onSend();
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      toast({ title: 'Email Queued', description: 'Email will be sent when email service is configured' });
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-500" />
            Send {config?.title} via Email
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-muted/50 p-3 rounded-lg text-sm">
            <p className="font-medium">{config?.title}: {docNumber}</p>
            <p className="text-muted-foreground">Client: {document?.client_name}</p>
            <p className="text-muted-foreground">Amount: {displaySymbol} {document?.total?.toFixed(2)}</p>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label>To *</Label>
              <Input
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                placeholder="recipient@email.com"
              />
            </div>
            <div>
              <Label>CC</Label>
              <Input
                type="email"
                value={emailData.cc}
                onChange={(e) => setEmailData({ ...emailData, cc: e.target.value })}
                placeholder="cc@email.com"
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={emailData.message}
                onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                rows={6}
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending}>
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Document Viewer Component with Language Toggle
const DocumentViewer = ({ 
  open, 
  onClose, 
  document, 
  type = 'invoice',
  template = null,
  companyInfo = null,
  onEmailSent = null
}) => {
  const printRef = useRef();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [documentLanguage, setDocumentLanguage] = useState('bilingual_en');
  const { toast } = useToast();
  const { currencySymbol, currencyCode, companyName, settings } = useSettings();
  
  const config = DOC_CONFIG[type];
  
  // Initialize document language from settings
  React.useEffect(() => {
    if (settings?.document_language) {
      setDocumentLanguage(settings.document_language);
    }
  }, [settings]);
  
  // Print handler using react-to-print v3 API
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${config.title}_${document?.quote_number || document?.order_number || document?.invoice_number}`,
    onAfterPrint: () => {
      toast({ title: 'Success', description: 'Document sent to printer' });
    },
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .font-arabic {
          font-family: 'Almarai', sans-serif !important;
        }
      }
    `
  });

  const handleDownloadPDF = () => {
    handlePrint();
    toast({ title: 'Info', description: 'Use "Save as PDF" in print dialog to download' });
  };

  if (!document) return null;

  const languageOptions = [
    { value: 'en', label: 'English Only', labelAr: 'English' },
    { value: 'ar', label: 'عربي فقط', labelAr: 'Arabic Only' },
    { value: 'bilingual_en', label: 'Bilingual (EN Primary)', labelAr: 'ثنائي اللغة' },
    { value: 'bilingual_ar', label: 'Bilingual (AR Primary)', labelAr: 'ثنائي (عربي أساسي)' },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-auto p-0">
          <DialogHeader className="p-4 border-b sticky top-0 bg-background z-10">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <config.icon className={`h-5 w-5 text-${config.color}-500`} />
                {config.title} - {document.quote_number || document.order_number || document.invoice_number}
                <Badge className={STATUS_COLORS[document.status]}>{document.status}</Badge>
              </DialogTitle>
              <div className="flex items-center gap-2">
                {/* Language Toggle */}
                <div className="flex items-center gap-2 mr-4">
                  <Languages className="h-4 w-4 text-muted-foreground" />
                  <Select value={documentLanguage} onValueChange={setDocumentLanguage}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select Language" />
                    </SelectTrigger>
                    <SelectContent>
                      {languageOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(true)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
                  <Download className="h-4 w-4 mr-2" />
                  PDF
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {/* Document Preview */}
          <div className="bg-gray-100 p-4 overflow-auto">
            <div className="shadow-lg">
              <PrintableDocument
                ref={printRef}
                document={document}
                type={type}
                template={template}
                companyInfo={companyInfo}
                currencySymbol={currencySymbol}
                currencyCode={currencyCode}
                documentLanguage={documentLanguage}
                settings={settings}
              />
            </div>
          </div>
          
          <DialogFooter className="p-4 border-t">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <EmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        document={document}
        type={type}
        onSend={onEmailSent}
        currencySymbol={currencySymbol}
        companyName={companyName}
      />
    </>
  );
};

export default DocumentViewer;
export { PrintableDocument, EmailDialog, DOC_CONFIG, STATUS_COLORS, LABELS };
